import { useEffect, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { F0Button } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'
import { ArrowLeft } from '@factorialco/f0-react/icons/app'
import ArrowCycle from '@factorialco/f0-react/icons/app/ArrowCycle'
import {
  CurrencySelectorCurrencyFragment,
  MileageRateConfigurationFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import BasicDetails from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails'
import Files from 'modules/expenses/components/expenses/Detail/_newForm/Files'
import AdditionalInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/AdditionalInformation'
import BudgetAndProject from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/BudgetAndProject'
import CostCenterInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/CostCenters'
import ExpenseInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/ExpenseInformation/f0'
import MileageInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/MileageInformation/f0'
import TripInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/TripInformation/f0'
import { MileageScopedEditSection } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/types'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { ProjectWorker } from 'modules/expenses/hooks/useProjects'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import { Expensable } from 'modules/expenses/types/Expensable'
import { BudgetOption } from 'modules/finance/hooks/useBudgets'

interface F0NewMileageFormProps {
  customizationFields: CustomizationField[]
  expensable?: Expensable
  budgetOptions: BudgetOption[]
  projectWorkers: ProjectWorker[]
  hasProjectsFeature: boolean
  loadingProjectWorkers: boolean
  loadingBudgetOptions: boolean
  canEditExpensesWithPendingStatus: boolean
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  onEmployeeIdChange: (employeeId: string | undefined) => void
  onEffectiveOnChange: (value: string) => void
  hasReportOnBehalfFeature: boolean
  canCreateExpensesForOthers: boolean
  canSeeEmployeeLegalEntity: boolean
  rates?: MileageRateConfigurationFragment[]
  currencies?: CurrencySelectorCurrencyFragment[]
  scrollTo?: string | undefined
  onReuseTrip?: () => void
  editSection?: MileageScopedEditSection
  onBackToConfirmation?: () => void
}

const F0NewMileageForm = (props: F0NewMileageFormProps) => {
  const { section } = useOutletContext<RouteContextParams>()
  const {
    expensable,
    budgetOptions,
    projectWorkers,
    hasProjectsFeature,
    loadingProjectWorkers,
    loadingBudgetOptions,
    registerDropHandler,
    onEmployeeIdChange,
    onEffectiveOnChange,
    rates,
    scrollTo,
    canEditExpensesWithPendingStatus,
    editSection,
    onBackToConfirmation,
  } = props

  const asReviewer = !isMyExpensesSection(section)

  const { employee } = useCurrentSession()

  // State to track distance calculation loading
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false)

  const tripInformationRef = useRef<HTMLDivElement>(null)
  const mileageInformationRef = useRef<HTMLDivElement>(null)
  const basicInformationRef = useRef<HTMLDivElement>(null)
  const budgetAndProjectRef = useRef<HTMLDivElement>(null)
  const additionalInformationRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // The timeout gives a more graceful animation experience
    setTimeout(() => {
      let ref: HTMLDivElement | null = null
      switch (scrollTo) {
        case 'basicInformation':
          ref = basicInformationRef.current
          break
        case 'tripInformation':
          ref = tripInformationRef.current
          break
        case 'mileageInformation':
          ref = mileageInformationRef.current
          break
        case 'budgetAndProject':
          ref = budgetAndProjectRef.current
          break
        case 'additionalInformation':
          ref = additionalInformationRef.current
          break
      }
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 300)
  }, [scrollTo])

  // Scoped edit mode: render only the target section.
  // Each case returns the section component alone (no wrapper); the wrapper is
  // applied once below so the Stack padding isn't repeated for every branch.
  const renderScopedSection = (section: MileageScopedEditSection): JSX.Element | null => {
    const backButton = onBackToConfirmation ? (
      <F0Button
        onClick={onBackToConfirmation}
        size="sm"
        icon={ArrowLeft}
        hideLabel
        variant="outline"
        label={i18n.t(
          'expenses_v2.confirmation_screen.expense.per_diem_summary_block.back_to_summary'
        )}
      />
    ) : undefined

    switch (section) {
      case 'basicInformation':
        return (
          <ExpenseInformation
            {...props}
            currentEmployee={employee}
            resourceType={'mileage'}
            asReviewer={asReviewer}
            onEmployeeIdChange={onEmployeeIdChange}
            isAutomaticExpensable={false}
            documentType={'default'}
            onEffectiveOnChange={onEffectiveOnChange}
            divider={false}
            rightAction={backButton}
          />
        )
      case 'tripInformation':
        return (
          <TripInformation
            {...props}
            currentEmployee={employee}
            resourceType={'mileage'}
            asReviewer={asReviewer}
            documentType={'default'}
            setIsCalculatingDistance={setIsCalculatingDistance}
            divider={false}
            rightAction={backButton}
          />
        )
      case 'mileageInformation':
        return (
          <MileageInformation
            {...props}
            documentType={'default'}
            currentEmployee={employee}
            resourceType={'mileage'}
            asReviewer={asReviewer}
            rates={rates}
            isCalculatingDistance={isCalculatingDistance}
            setIsCalculatingDistance={setIsCalculatingDistance}
            divider={false}
            rightAction={backButton}
          />
        )
      case 'budgetAndProject':
        return (
          <BudgetAndProject
            {...props}
            currentEmployee={employee}
            resourceType={'mileage'}
            asReviewer={asReviewer}
            documentType={'default'}
            budgetOptions={budgetOptions}
            projectWorkers={projectWorkers}
            hasProjectsFeature={hasProjectsFeature}
            loadingProjectWorkers={loadingProjectWorkers}
            loadingBudgetOptions={loadingBudgetOptions}
            divider={false}
            rightAction={backButton}
          />
        )
      case 'costCenters':
        return (
          <CostCenterInformation
            {...props}
            currentEmployee={employee}
            resourceId={expensable?.id}
            asReviewer={asReviewer}
            resourceType={'mileage'}
            documentType={'default'}
            divider={false}
            rightAction={backButton}
          />
        )
      case 'additionalInformation':
        return (
          <AdditionalInformation
            {...props}
            currentEmployee={employee}
            resourceType={'mileage'}
            asReviewer={asReviewer}
            documentType={'default'}
            expensable={expensable}
            divider={false}
            rightAction={backButton}
          />
        )
      case 'files':
        return (
          <Files
            {...props}
            currentEmployee={employee}
            fieldIdentifier="files"
            resourceType={'mileage'}
            asReviewer={asReviewer}
            documentType={'default'}
            expensable={expensable}
            registerDropHandler={registerDropHandler}
            taxAgencyOrganization={null}
            divider={false}
            rightAction={backButton}
          />
        )
      default:
        return null
    }
  }

  if (editSection && onBackToConfirmation) {
    const content = renderScopedSection(editSection)
    if (content) {
      return (
        <Stack gap="6" style={{ paddingTop: '24px', paddingBottom: '24px' }}>
          {content}
        </Stack>
      )
    }
  }

  const reuseButton = props.onReuseTrip ? (
    <F0Button
      variant="ghost"
      icon={ArrowCycle}
      label={i18n.t('expenses_v2.forms.new_form.reuse_trip.button')}
      onClick={props.onReuseTrip}
    />
  ) : undefined

  return (
    <Stack
      gap="6"
      style={{
        paddingTop: expensable ? '24px' : '8px',
        paddingBottom: expensable ? '24px' : '8px',
      }}
    >
      <BasicDetails
        expensable={expensable}
        canEditExpensesWithPendingStatus={canEditExpensesWithPendingStatus}
        rightAction={reuseButton}
      />
      <div ref={basicInformationRef}>
        <ExpenseInformation
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          onEmployeeIdChange={onEmployeeIdChange}
          isAutomaticExpensable={false}
          documentType={'default'}
          onEffectiveOnChange={onEffectiveOnChange}
        />
      </div>
      <div ref={tripInformationRef}>
        <TripInformation
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          documentType={'default'}
          setIsCalculatingDistance={setIsCalculatingDistance}
        />
      </div>
      <div ref={mileageInformationRef}>
        <MileageInformation
          {...props}
          documentType={'default'}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          rates={rates}
          isCalculatingDistance={isCalculatingDistance}
          setIsCalculatingDistance={setIsCalculatingDistance}
        />
      </div>
      <div ref={budgetAndProjectRef}>
        <BudgetAndProject
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          documentType={'default'}
          budgetOptions={budgetOptions}
          projectWorkers={projectWorkers}
          hasProjectsFeature={hasProjectsFeature}
          loadingProjectWorkers={loadingProjectWorkers}
          loadingBudgetOptions={loadingBudgetOptions}
        />
      </div>
      <div>
        <CostCenterInformation
          {...props}
          currentEmployee={employee}
          resourceId={expensable?.id}
          asReviewer={asReviewer}
          resourceType={'mileage'}
          documentType={'default'}
        />
      </div>
      <div ref={additionalInformationRef}>
        <AdditionalInformation
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          documentType={'default'}
          expensable={expensable}
        />
      </div>
      <Files
        {...props}
        currentEmployee={employee}
        fieldIdentifier="files"
        resourceType={'mileage'}
        asReviewer={asReviewer}
        documentType={'default'}
        expensable={expensable}
        registerDropHandler={registerDropHandler}
        taxAgencyOrganization={null}
      />
    </Stack>
  )
}
export default F0NewMileageForm
